import type { AppUser } from '../stores/authStore';

// ─── types ───────────────────────────────────────────────────────────────────
interface EducationEntry {
  id: string;
  type: 'COLLEGE' | 'GRADUATE';
  year: string;
  schoolName: string;
  major: string;
  status: 'GRADUATED' | 'COMPLETED' | 'ENROLLED';
  degreeLevel?: 'MASTERS' | 'DOCTORATE';
  degreeReviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
}
interface ExperienceEntry {
  id: string;
  year: string;
  company: string;
  position: string;
}
interface AwardEntry {
  id: string;
  year: string;
  title: string;
}
interface CertificateEntry {
  id: string;
  name: string;
  issuer: string;
  date: string;
  reviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
}
interface Visibility {
  education: boolean;
  experience: boolean;
  awards: boolean;
  certificates_list: boolean;
  introduction: boolean;
  stampfit_certificates: boolean;
  portfolio: boolean;
}

export interface ResumeTemplate1Props {
  user: AppUser | null;
  photoUrl: string | null;
  education: EducationEntry[];
  experience: ExperienceEntry[];
  awards: AwardEntry[];
  certificatesList: CertificateEntry[];
  stampfitCerts: any[];
  introduction: string;
  visibility: Visibility;
  desiredPosition: string;
  address: string;
  getPortfolioDisplayUrl: (slot: number) => string;
}

// ─── A4 dimensions (96 dpi) ──────────────────────────────────────────────────
const PAGE_W = 794;
const PAGE_H = 1123;
const LEFT_W = 263;
const RIGHT_W = PAGE_W - LEFT_W;   // 531
const NOTO = "'Noto Sans KR', sans-serif";
const DARK_BG = '#1D2B3A';
const MAX_ITEMS = 7;

// ─── Fixed layout positions ───────────────────────────────────────────────────

// Left column
const LC = {
  PHOTO_D: 185,                     // photo circle diameter
  PHOTO_TOP: 22,
  INFO_TITLE: 230,                  // "기본 정보"
  INFO_ITEMS: 272,                  // first info item
  DIV: 536,                         // FIXED horizontal divider
  EDU_TITLE: 556,                   // "학력"
  EDU_ITEMS: 598,                   // education content start
};

// Right column — left/right padding
const RP_L = 30;
const RP_R = 24;

// Page 1 right column fixed Y positions
const P1R = {
  NAME: 28,
  POS: 70,        // 이름 바로 아래
  DIV1: 130,      // 모집포지션 아래 간격 확보
  INTRO_T: 150,
  INTRO_C: 184,
  INTRO_H: 278,   // → bottom = 184+278 = 462px
  DIV2: 472,      // FIXED
  EXP_T: 492,
  EXP_C: 534,
  EXP_H: 170,     // → bottom = 534+170 = 704px
  DIV3: 714,      // FIXED
  AWARD_T: 734,
  AWARD_C: 780,
};

// Page 2 right column fixed Y positions
const P2R = {
  NAME: 28,
  POS: 70,
  DIV1: 130,      // P1R와 동일
  CERT_T: 150,
  CERT_C: 194,
  CERT_H: 404,    // → bottom = 194+404 = 598px
  DIV2: 608,      // FIXED
  SF_T: 628,
  SF_C: 674,
};

// ─── helpers ─────────────────────────────────────────────────────────────────
const STATUS_LABEL: Record<string, string> = {
  GRADUATED: '졸업', COMPLETED: '수료', ENROLLED: '재학',
};
const DEGREE_LABEL: Record<string, string> = {
  MASTERS: '석사', DOCTORATE: '박사',
};

function formatKoreanName(name?: string): string {
  if (!name) return '';
  const clean = name.replace(/\s/g, '');
  return clean.length <= 4 ? clean.split('').join(' ') : name;
}

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .trim();
}

// ─── sub-components ──────────────────────────────────────────────────────────

function Badge({ label = '인증', variant = 'default' }: { label?: string; variant?: 'default' | 'degree' }) {
  const isDegree = variant === 'degree';
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      background: isDegree ? '#eaf7ec' : '#F5821F',
      color: isDegree ? '#236e2b' : '#fff',
      fontSize: 10,
      fontWeight: 700,
      padding: isDegree ? '3px 9px 3px 6px' : '2px 7px 2px 5px',
      borderRadius: isDegree ? 20 : 3,
      border: isDegree ? '1px solid #a3d9a5' : 'none',
      verticalAlign: 'middle',
      lineHeight: 1.2,
      fontFamily: NOTO,
      flexShrink: 0,
      whiteSpace: 'nowrap',
    }}>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
        {isDegree ? (
          // 원형 체크 아이콘 (학위인증)
          <>
            <circle cx="5" cy="5" r="4.2" stroke="#236e2b" strokeWidth="1.2" fill="none"/>
            <path d="M2.8 5.1L4.2 6.5L7.2 3.5" stroke="#236e2b" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </>
        ) : (
          <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        )}
      </svg>
      {label}
    </span>
  );
}

// Shared right-column name + position header
function RightHeader({ user, desiredPosition }: { user: AppUser | null; desiredPosition: string }) {
  const nameEn = user?.name_en;
  return (
    <>
      <div style={{
        position: 'absolute',
        top: P1R.NAME,
        left: RP_L,
        right: RP_R,
        fontFamily: NOTO,
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 34, fontWeight: 700, color: '#111', lineHeight: 1.1 }}>
          {formatKoreanName(user?.name)}
        </span>
        {nameEn && (
          <span style={{ fontSize: 34, fontWeight: 700, color: '#111', lineHeight: 1.1 }}>
            ({nameEn})
          </span>
        )}
      </div>
      {desiredPosition && (
        <div style={{
          position: 'absolute',
          top: P1R.POS,
          left: RP_L,
          right: RP_R,
          fontSize: 28,
          fontWeight: 600,
          color: '#555',
          fontFamily: NOTO,
        }}>
          {desiredPosition}
        </div>
      )}
    </>
  );
}

// Half-width black horizontal line for right column
const RDIV_W = Math.round((RIGHT_W - RP_L - RP_R) / 2); // ~238px
function RDiv({ top }: { top: number }) {
  return (
    <div style={{
      position: 'absolute',
      top,
      left: RP_L,
      width: RDIV_W,
      height: 2.5,
      background: '#111',
    }} />
  );
}

// ─── Left column (shared Pages 1 & 2) ────────────────────────────────────────
function LeftCol({
  user, photoUrl, education, visibility, address,
}: {
  user: AppUser | null;
  photoUrl: string | null;
  education: EducationEntry[];
  visibility: Visibility;
  address: string;
}) {
  const formatBirthDate = (raw?: string) => {
    if (!raw) return '';
    // YYYY-MM-DD → YYYY.MM.DD
    return raw.replace(/-/g, '.');
  };

  const formatPhone = (raw?: string) => {
    if (!raw) return '';
    const d = raw.replace(/\D/g, '');
    if (d.length === 11) return `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`;
    if (d.length === 10) return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
    return raw;
  };

  const infoItems = [
    { label: 'Date of Birth', value: formatBirthDate(user?.birth_date) },
    { label: 'Phone', value: formatPhone(user?.phone) },
    { label: 'Addressee', value: address || '' },
    { label: 'E-mail', value: user?.email || '' },
  ].filter(i => !!i.value);

  const colleges = education.filter(e => e.type === 'COLLEGE' && e.year && e.schoolName);
  const graduates = education.filter(e => e.type === 'GRADUATE' && e.year && e.schoolName);

  const photoLeft = (LEFT_W - LC.PHOTO_D) / 2;

  return (
    <div style={{
      position: 'absolute',
      left: 0, top: 0,
      width: LEFT_W, height: PAGE_H,
      overflow: 'hidden',
      fontFamily: NOTO,
      color: '#fff',
    }}>
      {/* Photo circle */}
      <div style={{
        position: 'absolute',
        left: photoLeft,
        top: LC.PHOTO_TOP,
        width: LC.PHOTO_D,
        height: LC.PHOTO_D,
        borderRadius: '50%',
        overflow: 'hidden',
        background: '#4A6580',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {photoUrl && (
          <img
            src={photoUrl}
            alt="프로필"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
          />
        )}
      </div>

      {/* 기본 정보 title — fixed */}
      <div style={{
        position: 'absolute',
        top: LC.INFO_TITLE,
        left: 22, right: 10,
        fontSize: 20,
        fontWeight: 600,
        letterSpacing: 0.5,
        color: '#fff',
      }}>
        기본 정보
      </div>

      {/* Info items */}
      <div style={{
        position: 'absolute',
        top: LC.INFO_ITEMS,
        left: 22, right: 10,
        height: LC.DIV - LC.INFO_ITEMS - 16,
        overflow: 'hidden',
      }}>
        {infoItems.map((item, idx) => (
          <div key={item.label} style={{ marginBottom: idx < infoItems.length - 1 ? 18 : 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 3 }}>
              {item.label}
            </div>
            <div style={{
              fontSize: 12,
              fontWeight: 400,
              color: '#fff',
              lineHeight: 1.4,
              wordBreak: 'break-all',
            }}>
              {item.value}
            </div>
          </div>
        ))}
      </div>

      {/* Left divider — FIXED */}
      {visibility.education && (
        <div style={{
          position: 'absolute',
          top: LC.DIV,
          left: 22, right: 22,
          height: 2,
          background: '#fff',
        }} />
      )}

      {/* 학력 section — fixed start, clips at bottom */}
      {visibility.education && (
        <>
          <div style={{
            position: 'absolute',
            top: LC.EDU_TITLE,
            left: 22, right: 10,
            fontSize: 20,
            fontWeight: 600,
            letterSpacing: 0.5,
            color: '#fff',
          }}>
            학력
          </div>

          <div style={{
            position: 'absolute',
            top: LC.EDU_ITEMS,
            left: 22, right: 10,
            bottom: 16,
            overflow: 'hidden',
          }}>
            {colleges.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
                  대학교
                </div>
                {colleges.map(edu => (
                  <div key={edu.id} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>
                        {edu.year} {edu.schoolName}
                      </span>
                      {edu.degreeReviewStatus === 'APPROVED' && (
                        <Badge label="학위 인증 완료" variant="degree" />
                      )}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.78)', marginTop: 3 }}>
                      {edu.major} {STATUS_LABEL[edu.status] || ''}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {graduates.length > 0 && (
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
                  대학원
                </div>
                {graduates.map(edu => (
                  <div key={edu.id} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>
                        {edu.year} {edu.schoolName}
                      </span>
                      {edu.degreeReviewStatus === 'APPROVED' && (
                        <Badge label="학위 인증 완료" variant="degree" />
                      )}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.78)', marginTop: 3 }}>
                      {edu.major}
                      {edu.degreeLevel ? ` ${DEGREE_LABEL[edu.degreeLevel]}` : ''}{' '}
                      {STATUS_LABEL[edu.status] || ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Page wrapper ─────────────────────────────────────────────────────────────
function PageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="resume-page"
      style={{
        width: PAGE_W,
        height: PAGE_H,
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* <img> 로 배경 출력 — CSS backgroundImage는 인쇄 시 브라우저가 기본 차단 */}
      <img
        src="/port_bg_1.png"
        alt=""
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          objectFit: 'fill',
          zIndex: 0,
          display: 'block',
        }}
      />
      <div style={{ position: 'relative', zIndex: 1, width: '100%', height: '100%' }}>
        {children}
      </div>
    </div>
  );
}

// ─── Right column white overlay ───────────────────────────────────────────────
function RightOverlay({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      position: 'absolute',
      left: LEFT_W,
      top: 0,
      width: RIGHT_W,
      height: PAGE_H,
      background: '#fff',
      overflow: 'hidden',
      fontFamily: NOTO,
    }}>
      {children}
    </div>
  );
}

// ─── Page 1 ───────────────────────────────────────────────────────────────────
function Page1(props: ResumeTemplate1Props) {
  const { user, photoUrl, education, experience, awards, introduction, visibility, desiredPosition, address } = props;

  const filteredExp = experience.filter(e => e.year && e.company);
  const filteredAwards = awards.filter(a => a.year && a.title);
  const expSlice = filteredExp.slice(0, MAX_ITEMS);
  const expExtra = filteredExp.length - expSlice.length;
  const awardSlice = filteredAwards.slice(0, MAX_ITEMS);
  const awardExtra = filteredAwards.length - awardSlice.length;

  return (
    <PageWrapper>
      <LeftCol user={user} photoUrl={photoUrl} education={education} visibility={visibility} address={address} />

      <RightOverlay>
        {/* Name + Position */}
        <RightHeader user={user} desiredPosition={desiredPosition} />

        {/* Divider 1 */}
        <RDiv top={P1R.DIV1} />

        {/* 자기소개 — FIXED area */}
        {visibility.introduction && (
          <>
            <div style={{
              position: 'absolute',
              top: P1R.INTRO_T,
              left: RP_L,
              fontSize: 24,
              fontWeight: 600,
              color: '#111',
            }}>
              자기소개
            </div>
            <div style={{
              position: 'absolute',
              top: P1R.INTRO_C,
              left: RP_L,
              right: RP_R,
              height: P1R.INTRO_H,
              overflow: 'hidden',
              fontSize: 13,
              fontWeight: 200,
              color: '#333',
              lineHeight: 1.8,
              whiteSpace: 'pre-wrap',
            }}>
              {stripHtml(introduction)}
            </div>
          </>
        )}

        {/* Divider 2 — FIXED */}
        <RDiv top={P1R.DIV2} />

        {/* 경력 — FIXED area */}
        {visibility.experience && (
          <>
            <div style={{
              position: 'absolute',
              top: P1R.EXP_T,
              left: RP_L,
              fontSize: 24,
              fontWeight: 600,
              color: '#111',
            }}>
              경력
            </div>
            <div style={{
              position: 'absolute',
              top: P1R.EXP_C,
              left: RP_L,
              right: RP_R,
              height: P1R.EXP_H,
              overflow: 'hidden',
            }}>
              {expSlice.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'baseline', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#888', minWidth: 130, flexShrink: 0 }}>
                    {item.year}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#111', marginRight: 10 }}>
                    {item.company}
                  </span>
                  {item.position && (
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#555' }}>
                      {item.position}
                    </span>
                  )}
                </div>
              ))}
              {expExtra > 0 && (
                <div style={{ fontSize: 12, color: '#aaa', fontWeight: 600 }}>그 외 +{expExtra}개</div>
              )}
            </div>
          </>
        )}

        {/* Divider 3 — FIXED */}
        <RDiv top={P1R.DIV3} />

        {/* 수상 내역 및 위촉 사항 — FIXED area */}
        {visibility.awards && (
          <>
            <div style={{
              position: 'absolute',
              top: P1R.AWARD_T,
              left: RP_L,
              fontSize: 24,
              fontWeight: 600,
              color: '#111',
            }}>
              수상 내역 및 위촉 사항
            </div>
            <div style={{
              position: 'absolute',
              top: P1R.AWARD_C,
              left: RP_L,
              right: RP_R,
              bottom: 24,
              overflow: 'hidden',
            }}>
              {awardSlice.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'baseline', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#888', minWidth: 130, flexShrink: 0 }}>
                    {item.year}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>
                    {item.title}
                  </span>
                </div>
              ))}
              {awardExtra > 0 && (
                <div style={{ fontSize: 12, color: '#aaa', fontWeight: 600 }}>그 외 +{awardExtra}개</div>
              )}
            </div>
          </>
        )}
      </RightOverlay>
    </PageWrapper>
  );
}

// ─── Page 2 ───────────────────────────────────────────────────────────────────
function Page2(props: ResumeTemplate1Props) {
  const { user, photoUrl, education, certificatesList, stampfitCerts, visibility, desiredPosition, address } = props;

  const filteredCerts = certificatesList.filter(c => c.name);

  return (
    <PageWrapper>
      <LeftCol user={user} photoUrl={photoUrl} education={education} visibility={visibility} address={address} />

      <RightOverlay>
        {/* Name + Position — same as page 1 */}
        <RightHeader user={user} desiredPosition={desiredPosition} />
        <RDiv top={P2R.DIV1} />

        {/* 보유 자격증 및 수료 내역 — FIXED area */}
        {visibility.certificates_list && (
          <>
            <div style={{
              position: 'absolute',
              top: P2R.CERT_T,
              left: RP_L,
              fontSize: 24,
              fontWeight: 600,
              color: '#111',
            }}>
              보유 자격증 및 수료 내역
            </div>
            <div style={{
              position: 'absolute',
              top: P2R.CERT_C,
              left: RP_L,
              right: RP_R,
              height: P2R.CERT_H,
              overflow: 'hidden',
            }}>
              {filteredCerts.slice(0, MAX_ITEMS).map(item => (
                <div key={item.id} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    {item.date && (
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>{item.date}</span>
                    )}
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{item.name}</span>
                    {item.reviewStatus === 'APPROVED' && <Badge label="인증" />}
                  </div>
                  {item.issuer && (
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#777', marginTop: 3 }}>
                      {item.issuer}
                    </div>
                  )}
                </div>
              ))}
              {filteredCerts.length > MAX_ITEMS && (
                <div style={{ fontSize: 12, color: '#aaa', fontWeight: 600 }}>
                  그 외 +{filteredCerts.length - MAX_ITEMS}개
                </div>
              )}
            </div>
          </>
        )}

        {/* Divider 2 — FIXED */}
        <RDiv top={P2R.DIV2} />

        {/* STAMP FIT 인증 교육 내역 — FIXED area */}
        {visibility.stampfit_certificates && (
          <>
            <div style={{
              position: 'absolute',
              top: P2R.SF_T,
              left: RP_L,
              fontSize: 24,
              fontWeight: 600,
              color: '#111',
            }}>
              STAMP FIT 인증 교육 내역
            </div>
            <div style={{
              position: 'absolute',
              top: P2R.SF_C,
              left: RP_L,
              right: RP_R,
              bottom: 28,
              overflow: 'hidden',
            }}>
              {stampfitCerts.slice(0, MAX_ITEMS).map((cert: any) => {
                const offH = cert.class?.offline_hours || 0;
                const onH = cert.class?.online_hours || 0;
                const hrs = [offH > 0 ? `오프라인 ${offH}h` : null, onH > 0 ? `온라인 ${onH}h` : null]
                  .filter(Boolean).join(' / ');
                const issued = cert.issued_at
                  ? new Date(cert.issued_at).toLocaleDateString('ko-KR', { year: 'numeric', month: 'numeric', day: 'numeric' })
                  : '';
                return (
                  <div key={cert.id} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                      {issued && (
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>{issued}</span>
                      )}
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{cert.class_name}</span>
                      <Badge label="인증" />
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#777', marginTop: 4 }}>
                      {[cert.provider?.company_name, hrs].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                );
              })}
              {stampfitCerts.length > MAX_ITEMS && (
                <div style={{ fontSize: 12, color: '#aaa', fontWeight: 600 }}>
                  그 외 +{stampfitCerts.length - MAX_ITEMS}개
                </div>
              )}
            </div>
          </>
        )}
      </RightOverlay>
    </PageWrapper>
  );
}

// ─── Page 3 ───────────────────────────────────────────────────────────────────
function Page3(props: ResumeTemplate1Props) {
  const { visibility, getPortfolioDisplayUrl } = props;
  const images = [0, 1, 2].map(i => getPortfolioDisplayUrl(i)).filter(Boolean);

  const PADDING = 24;
  const GAP = 16;
  const totalGap = GAP * (Math.max(images.length, 1) - 1);
  const imgH = Math.floor((PAGE_H - PADDING * 2 - totalGap) / Math.max(images.length, 1));
  const imgW = RIGHT_W - PADDING * 2;

  return (
    <PageWrapper>
      {/* LEFT — "포트폴리오" label only */}
      <div style={{
        position: 'absolute',
        left: 0, top: 0,
        width: LEFT_W, height: PAGE_H,
        padding: '28px 22px',
        fontFamily: NOTO,
      }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: '#fff', letterSpacing: 0.5 }}>
          포트폴리오
        </span>
      </div>

      {/* RIGHT */}
      {visibility.portfolio && (
        <div style={{
          position: 'absolute',
          left: LEFT_W, top: 0,
          width: RIGHT_W, height: PAGE_H,
          background: '#fff',
          padding: `${PADDING}px`,
          overflow: 'hidden',
        }}>
          {images.length === 0 ? (
            <div style={{ color: '#ccc', fontSize: 14, fontFamily: NOTO, marginTop: 40, textAlign: 'center' }}>
              포트폴리오 이미지가 없습니다.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: GAP }}>
              {images.map((url, idx) => (
                <div
                  key={idx}
                  style={{
                    width: imgW,
                    height: imgH,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    borderRadius: 10,
                    background: '#f3f4f6',
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={url}
                    alt={`포트폴리오 ${idx + 1}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: 10,
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </PageWrapper>
  );
}

// ─── Root export ──────────────────────────────────────────────────────────────
export default function ResumeTemplate1Preview(props: ResumeTemplate1Props) {
  return (
    <div
      id="resume-template-1"
      className="resume-template-root"
      style={{ fontFamily: NOTO, display: 'flex', flexDirection: 'column', gap: 24 }}
    >
      <Page1 {...props} />
      <Page2 {...props} />
      <Page3 {...props} />
    </div>
  );
}
